/**
 * Call Center tool registry.
 *
 * Lets Kai (and any future Call Center agent) answer "what's going on
 * with customer X" questions by hitting atlas_warehouse.cc_* tables
 * directly through Supabase + RLS. Each tool takes tight inputs and
 * returns compact, agent-digestible shapes — no sprawling rows.
 */

import { defineTool, type AgentTool } from '../agent';

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyClient = {
  from: (table: string) => any;
  schema: (name: string) => AnyClient;
  rpc: (name: string, args?: Record<string, unknown>) => any;
};

export function buildCallCenterTools(supabase: AnyClient): AgentTool[] {
  const warehouse = supabase.schema('atlas_warehouse');

  const findCustomer = defineTool<
    { query: string; limit?: number },
    Array<{ id: string; full_name: string; email: string | null; phone: string | null }>
  >({
    name: 'findCustomer',
    description:
      'Resolve a customer by name, email, or phone. Use FIRST before calling any other Call Center tool.',
    inputSchema: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Search text — name fragment / email / phone digits' },
        limit: { type: 'integer', description: 'Max rows to return. Defaults to 5.' },
      },
      required: ['query'],
    },
    execute: async (input) => {
      const q = input.query.trim();
      const limit = input.limit ?? 5;
      const isEmail = q.includes('@');
      const isPhone = /^\+?\d/.test(q);
      let query = warehouse
        .from('cc_customers')
        .select('id, full_name, email, phone');
      if (isEmail) {
        query = query.ilike('email', `%${q}%`);
      } else if (isPhone) {
        query = query.ilike('phone', `%${q}%`);
      } else {
        query = query.ilike('full_name', `%${q}%`);
      }
      const { data, error } = await query.limit(limit);
      if (error) throw new Error(error.message);
      return (data ?? []) as any;
    },
  });

  const getCustomerSummary = defineTool<
    { customer_id: string },
    {
      full_name: string;
      email: string | null;
      phone: string | null;
      calls_last_90d: number;
      open_tickets: number;
      active_booking_id: number | null;
      unpaid_fines_aed: number | null;
    }
  >({
    name: 'getCustomerSummary',
    description:
      'Single-shot snapshot of a customer: call count, open tickets, active booking, unpaid fines. Use for overview questions.',
    inputSchema: {
      type: 'object',
      properties: { customer_id: { type: 'string', description: 'UUID from findCustomer.' } },
      required: ['customer_id'],
    },
    execute: async ({ customer_id }) => {
      const { data, error } = await supabase
        .from('cc_customer_360')
        .select('full_name, email, phone, calls_last_90d, open_tickets, active_booking_id, unpaid_fines_aed')
        .eq('customer_id', customer_id)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data ?? { full_name: '(unknown)', email: null, phone: null, calls_last_90d: 0, open_tickets: 0, active_booking_id: null, unpaid_fines_aed: 0 }) as any;
    },
  });

  const getRecentCalls = defineTool<
    { customer_id: string; limit?: number },
    Array<{ started_at: string; agent_name: string | null; topic: string | null; summary: string | null; sentiment: string }>
  >({
    name: 'getRecentCalls',
    description: 'Most recent calls for a customer, newest first. Default limit 5.',
    inputSchema: {
      type: 'object',
      properties: {
        customer_id: { type: 'string' },
        limit: { type: 'integer' },
      },
      required: ['customer_id'],
    },
    execute: async ({ customer_id, limit }) => {
      const { data, error } = await warehouse
        .from('cc_calls')
        .select('started_at, agent_name, topic, summary, sentiment')
        .eq('customer_id', customer_id)
        .order('started_at', { ascending: false })
        .limit(limit ?? 5);
      if (error) throw new Error(error.message);
      return (data ?? []) as any;
    },
  });

  const getOpenTickets = defineTool<
    { customer_id: string },
    Array<{ id: number; status: string; priority: string; subject: string; body: string | null; created_at: string }>
  >({
    name: 'getOpenTickets',
    description: 'All tickets in open / pending status for a customer.',
    inputSchema: {
      type: 'object',
      properties: { customer_id: { type: 'string' } },
      required: ['customer_id'],
    },
    execute: async ({ customer_id }) => {
      const { data, error } = await warehouse
        .from('cc_tickets')
        .select('id, status, priority, subject, body, created_at')
        .eq('customer_id', customer_id)
        .in('status', ['open', 'pending'])
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as any;
    },
  });

  const getActiveBooking = defineTool<
    { customer_id: string },
    {
      id: number;
      car_make: string;
      car_model: string;
      plate: string | null;
      pickup_city: string | null;
      delivery_address: string | null;
      delivered_at: string | null;
      pickup_at: string | null;
      return_at: string | null;
      daily_rate_aed: number | null;
    } | null
  >({
    name: 'getActiveBooking',
    description:
      'The customer\'s currently active booking — car, plate, delivery address, pickup/return times, daily rate.',
    inputSchema: {
      type: 'object',
      properties: { customer_id: { type: 'string' } },
      required: ['customer_id'],
    },
    execute: async ({ customer_id }) => {
      const { data, error } = await warehouse
        .from('cc_bookings')
        .select('id, car_make, car_model, plate, pickup_city, delivery_address, delivered_at, pickup_at, return_at, daily_rate_aed')
        .eq('customer_id', customer_id)
        .eq('status', 'active')
        .order('pickup_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data ?? null) as any;
    },
  });

  const getPaymentHistory = defineTool<
    { customer_id: string; days?: number },
    Array<{
      id: number;
      amount_aed: number;
      charged_at: string;
      descriptor: string | null;
      status: string;
      payment_method: string | null;
    }>
  >({
    name: 'getPaymentHistory',
    description:
      'Recent payments for a customer — use to explain "what\'s that charge on my card" questions.',
    inputSchema: {
      type: 'object',
      properties: {
        customer_id: { type: 'string' },
        days: { type: 'integer', description: 'Lookback window. Defaults to 60.' },
      },
      required: ['customer_id'],
    },
    execute: async ({ customer_id, days }) => {
      const since = new Date(Date.now() - (days ?? 60) * 86400_000).toISOString();
      const { data, error } = await warehouse
        .from('cc_payments')
        .select('id, amount_aed, charged_at, descriptor, status, payment_method')
        .eq('customer_id', customer_id)
        .gte('charged_at', since)
        .order('charged_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as any;
    },
  });

  const getTrafficFines = defineTool<
    { customer_id: string; include_paid?: boolean },
    Array<{ id: number; amount_aed: number; issued_at: string; description: string | null; paid: boolean }>
  >({
    name: 'getTrafficFines',
    description: 'Traffic fines issued for the customer. By default only unpaid ones.',
    inputSchema: {
      type: 'object',
      properties: {
        customer_id: { type: 'string' },
        include_paid: { type: 'boolean' },
      },
      required: ['customer_id'],
    },
    execute: async ({ customer_id, include_paid }) => {
      let q = warehouse
        .from('cc_traffic_fines')
        .select('id, amount_aed, issued_at, description, paid')
        .eq('customer_id', customer_id)
        .order('issued_at', { ascending: false });
      if (!include_paid) q = q.eq('paid', false);
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []) as any;
    },
  });

  return [
    findCustomer,
    getCustomerSummary,
    getRecentCalls,
    getOpenTickets,
    getActiveBooking,
    getPaymentHistory,
    getTrafficFines,
  ] as unknown as AgentTool[];
}

export const CALL_CENTER_TOOL_METADATA = [
  {
    name: 'findCustomer',
    label: 'Find customer',
    description: 'Resolve a person by name / email / phone.',
    requires: ['module:call-center'],
    readOrWrite: 'read' as const,
  },
  {
    name: 'getCustomerSummary',
    label: 'Customer summary',
    description: 'Overview: calls, tickets, active booking, unpaid fines.',
    requires: ['module:call-center'],
    readOrWrite: 'read' as const,
  },
  {
    name: 'getRecentCalls',
    label: 'Recent calls',
    description: 'Last N calls for a customer.',
    requires: ['module:call-center'],
    readOrWrite: 'read' as const,
  },
  {
    name: 'getOpenTickets',
    label: 'Open tickets',
    description: 'Open + pending tickets for a customer.',
    requires: ['module:call-center'],
    readOrWrite: 'read' as const,
  },
  {
    name: 'getActiveBooking',
    label: 'Active booking',
    description: 'Car + delivery address + pickup/return for the live booking.',
    requires: ['module:call-center'],
    readOrWrite: 'read' as const,
  },
  {
    name: 'getPaymentHistory',
    label: 'Payment history',
    description: 'Recent charges + descriptors — for "what\'s that charge" questions.',
    requires: ['module:call-center'],
    readOrWrite: 'read' as const,
  },
  {
    name: 'getTrafficFines',
    label: 'Traffic fines',
    description: 'Unpaid (default) or all fines for the customer.',
    requires: ['module:call-center'],
    readOrWrite: 'read' as const,
  },
];
